import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  Dumbbell,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import { exercisesAPI, trainersAPI } from "../services/api";
import { useApp } from "../contexts/AppContext";
import { useTheme } from "../contexts/ThemeContext";
import ExerciseModal from "../components/ui/ExerciseModal";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useCRUD } from "../hooks/useCRUD";
import { useTableData } from "../hooks/useTableData";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable/DataTable";
import FilterSidebar from "../components/common/FilterSidebar";

const ExercisesPage = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { selectedOrganizationId } = useApp();
  const navigate = useNavigate();

  // Modular hooks for CRUD operations
  const {
    data: exercises,
    setData: setExercises,
    loading,
    actionLoading,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    fetchAll: fetchExercises,
    create: createExercise,
    update: updateExercise,
    remove: removeExercise,
  } = useCRUD(exercisesAPI, selectedOrganizationId);

  // Additional state
  const [trainers, setTrainers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    exercise: null,
  });
  const [reviewStatusConfirm, setReviewStatusConfirm] = useState({
    isOpen: false,
    exercise: null,
    newStatus: null,
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  // Use table data hook with search
  const {
    paginatedData: paginatedExercises,
    totalItems: filteredCount,
    currentPage,
    rowsPerPage,
    totalPages,
    startIndex,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = useTableData(exercises, [
    "name",
    "muscleGroup",
    "equipment",
    "difficulty",
  ]);

  // Fetch trainers (memoized to prevent duplicate calls)
  // Fetch trainers (memoized to prevent duplicate calls)
  const fetchTrainers = useCallback(async () => {
    if (!selectedOrganizationId) return;
    try {
      const response = await trainersAPI.getAll(selectedOrganizationId);
      const trainersData = response.data.data || [];
      // Deduplicate trainers by ID to prevent React key warnings
      const uniqueTrainers = trainersData.filter(
        (trainer, index, self) =>
          index === self.findIndex((t) => t.id === trainer.id),
      );
      setTrainers(uniqueTrainers);
    } catch (err) {
      console.error("Error fetching trainers:", err);
    }
  }, [selectedOrganizationId]);

  // Fetch data on mount and when organization changes
  useEffect(() => {
    if (selectedOrganizationId) {
      fetchExercises();
      fetchTrainers();
    }
  }, [selectedOrganizationId, fetchExercises, fetchTrainers]);

  // CRUD handlers
  const handleCreate = async (exerciseData) => {
    const result = await createExercise(
      exerciseData,
      `${exerciseData.name} has been added to the system.`,
      "general",
    );
    if (result) {
      setIsModalOpen(false);
    }
  };

  const handleUpdate = async (exerciseData) => {
    const result = await updateExercise(
      editingExercise.id,
      exerciseData,
      `${exerciseData.name} has been updated successfully.`,
      "general",
    );
    if (result) {
      setIsModalOpen(false);
      setEditingExercise(null);
    }
  };

  const handleDelete = async () => {
    await removeExercise(
      deleteConfirm.exercise.id,
      `${deleteConfirm.exercise.name} has been deleted successfully.`,
      "general",
    );
    setDeleteConfirm({ isOpen: false, exercise: null });
  };

  const handleUpdateReviewStatus = async () => {
    try {
      const response = await exercisesAPI.updateReviewStatus(
        reviewStatusConfirm.exercise.id,
        reviewStatusConfirm.newStatus,
      );
      setExercises((prev) =>
        prev.map((exercise) =>
          exercise.id === reviewStatusConfirm.exercise.id
            ? response.data.data
            : exercise,
        ),
      );
      setReviewStatusConfirm({
        isOpen: false,
        exercise: null,
        newStatus: null,
      });
      setSuccessMessage(`Exercise has been ${reviewStatusConfirm.newStatus}.`);
    } catch (err) {
      console.error("Error updating review status:", err);
      setError("Failed to update review status");
    }
  };

  // Modal handlers
  const openEditModal = (exercise) => {
    setEditingExercise(exercise);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingExercise(null);
    setIsModalOpen(true);
  };

  // Helper functions
  const getCreatorName = (createdBy) => {
    const trainer = trainers.find((t) => t.id === createdBy);
    return trainer ? trainer.name : t("exercisesPage.creatorUnknown");
  };

  const getReviewStatusBadge = (status) => {
    const statusConfig = {
      approved: {
        bg: "bg-gradient-to-r from-green-500 to-green-600",
        text: "text-white",
        label: t("exercisesPage.status.approved"),
        icon: CheckCircle,
      },
      rejected: {
        bg: "bg-gradient-to-r from-red-500 to-red-600",
        text: "text-white",
        label: t("exercisesPage.status.rejected"),
        icon: XCircle,
      },
      pending: {
        bg: "bg-gradient-to-r from-yellow-500 to-yellow-600",
        text: "text-white",
        label: t("exercisesPage.status.pending"),
        icon: Clock,
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} shadow-sm`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getDifficultyBadge = (difficulty) => {
    const difficultyColors = {
      beginner:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      intermediate:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
      advanced: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    };
    const label =
      (`${difficulty}`,
      difficulty?.charAt(0).toUpperCase() + difficulty?.slice(1));

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${difficultyColors[difficulty] || difficultyColors.beginner}`}
      >
        {label}
      </span>
    );
  };

  // Filter options
  const filterOptions = [
    {
      key: "search",
      type: "search",
      label: t("exercisesPage.searchPlaceholder"),
      placeholder: t("exercisesPage.searchPlaceholder"),
      value: searchTerm,
      onChange: handleSearch,
    },
  ];

  // Table columns configuration
  const columns = [
    {
      key: "exercise",
      label: t("exercisesPage.table.exercise"),
      width: "2.5fr",
      render: (exercise) => (
        <div className="flex items-center space-x-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold shadow-lg flex-shrink-0`}
          >
            <Dumbbell className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className={`font-semibold ${colors.textPrimary} truncate text-sm`}
            >
              {exercise.name}
            </div>
            <div className={`text-xs ${colors.textTertiary} truncate`}>
              {exercise.muscleGroup}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "equipment",
      label: t("exercisesPage.table.equipment"),
      width: "1fr",
      render: (exercise) => (
        <span className={`text-xs ${colors.textSecondary} truncate block`}>
          {exercise.equipment || "None"}
        </span>
      ),
    },
    {
      key: "difficulty",
      label: t("exercisesPage.table.difficulty"),
      width: "0.8fr",
      render: (exercise) => getDifficultyBadge(exercise.difficulty),
    },
    {
      key: "status",
      label: t("exercisesPage.table.status"),
      width: "1fr",
      render: (exercise) => getReviewStatusBadge(exercise.reviewStatus),
    },
    {
      key: "creator",
      label: t("exercisesPage.table.createdBy"),
      width: "1.2fr",
      render: (exercise) => (
        <div className="flex flex-col">
          <span className={`text-xs ${colors.textSecondary} truncate block`}>
            {exercise.updatedByName || getCreatorName(exercise.createdBy)}
          </span>
          <span className={`text-[10px] ${colors.textMuted} truncate block`}>
            {exercise.updatedAt ? new Date(exercise.updatedAt).toLocaleDateString() : ""}
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      label: t("exercisesPage.table.actions"),
      width: "1.4fr",
      render: (exercise) => (
        <div className="flex items-center justify-end space-x-1">
          <button
            onClick={() => navigate(`/exercises/${exercise.id}`)}
            className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-sm transform hover:scale-105"
            title={t("common.viewDetails")}
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => openEditModal(exercise)}
            disabled={actionLoading}
            className="flex items-center justify-center w-7 h-7 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-all shadow-sm transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
            title={t("common.edit")}
          >
            <Edit className="w-3.5 h-3.5" />
          </button>
          {exercise.reviewStatus === "pending" && (
            <>
              <button
                onClick={() =>
                  setReviewStatusConfirm({
                    isOpen: true,
                    exercise,
                    newStatus: "approved",
                  })
                }
                disabled={actionLoading}
                className="flex items-center justify-center w-7 h-7 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-all shadow-sm transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
                title={t("common.approve")}
              >
                <CheckCircle className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() =>
                  setReviewStatusConfirm({
                    isOpen: true,
                    exercise,
                    newStatus: "rejected",
                  })
                }
                disabled={actionLoading}
                className="flex items-center justify-center w-7 h-7 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-all shadow-sm transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
                title={t("common.reject")}
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          <button
            onClick={() => setDeleteConfirm({ isOpen: true, exercise })}
            disabled={actionLoading}
            className="flex items-center justify-center w-7 h-7 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-all shadow-sm transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
            title={t("common.delete")}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ];

  // Mobile card renderer
  const renderMobileCard = (exercise) => (
    <div className={`p-5 ${colors.cardBg} ${colors.cardBorder} border rounded-2xl shadow-md mb-4 mx-1 transition-all active:scale-[0.99]`}>
      <div className="flex items-start space-x-3 mb-3">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold shadow-lg flex-shrink-0`}
        >
          <Dumbbell className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-semibold ${colors.textPrimary} truncate`}>
            {exercise.name}
          </div>
          <div className={`text-sm ${colors.textTertiary} truncate`}>
            {exercise.muscleGroup}
          </div>
        </div>
        <div>{getReviewStatusBadge(exercise.reviewStatus)}</div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
        <div className="flex items-center min-w-0">
          <span className={`${colors.textMuted} shrink-0`}>Equipment:</span>
          <span className={`ml-1 ${colors.textSecondary} truncate`} title={exercise.equipment || "None"}>
            {exercise.equipment || "None"}
          </span>
        </div>
        <div className="flex items-center min-w-0">
          <span className={`${colors.textMuted} shrink-0`}>Difficulty:</span>
          <span className="ml-1 truncate">
            {getDifficultyBadge(exercise.difficulty)}
          </span>
        </div>
      </div>

      <div className="mb-3 text-sm">
        <span className={colors.textMuted}>Updated by:</span>
        <span className={`ml-1 ${colors.textSecondary}`}>
          {exercise.updatedByName || getCreatorName(exercise.createdBy)}
        </span>
      </div>

      <div
        className={`flex items-center justify-around space-x-2 pt-4 border-t ${colors.border}`}
      >
        <button
          onClick={() => navigate(`/exercises/${exercise.id}`)}
          className={`flex-1 flex items-center justify-center p-2.5 ${colors.textSecondary} hover:text-blue-400 ${colors.bgHover} rounded-xl transition-colors`}
          title={t("common.viewDetails")}
        >
          <Eye className="w-5 h-5" />
        </button>
        <button
          onClick={() => openEditModal(exercise)}
          disabled={actionLoading}
          className={`flex-1 flex items-center justify-center p-2.5 ${colors.textSecondary} hover:text-orange-400 ${colors.bgHover} rounded-xl transition-colors disabled:opacity-50`}
          title={t("common.edit")}
        >
          <Edit className="w-5 h-5" />
        </button>
        {exercise.reviewStatus === "pending" && (
          <>
            <button
              onClick={() =>
                setReviewStatusConfirm({
                  isOpen: true,
                  exercise,
                  newStatus: "approved",
                })
              }
              disabled={actionLoading}
              className={`flex-1 flex items-center justify-center p-2.5 ${colors.textSecondary} hover:text-green-400 ${colors.bgHover} rounded-xl transition-colors disabled:opacity-50`}
              title={t("common.approve")}
            >
              <CheckCircle className="w-5 h-5" />
            </button>
            <button
              onClick={() =>
                setReviewStatusConfirm({
                  isOpen: true,
                  exercise,
                  newStatus: "rejected",
                })
              }
              disabled={actionLoading}
              className={`flex-1 flex items-center justify-center p-2.5 ${colors.textSecondary} hover:text-red-400 ${colors.bgHover} rounded-xl transition-colors disabled:opacity-50`}
              title={t("common.reject")}
            >
              <XCircle className="w-5 h-5" />
            </button>
          </>
        )}
        <button
          onClick={() => setDeleteConfirm({ isOpen: true, exercise })}
          disabled={actionLoading}
          className={`flex-1 flex items-center justify-center p-2.5 ${colors.textSecondary} hover:text-red-400 ${colors.bgHover} rounded-xl transition-colors disabled:opacity-50`}
          title={t("common.delete")}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  // Empty state
  if (!selectedOrganizationId) {
    return (
      <div className={`flex h-full ${colors.bgPrimary} p-6`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12 max-w-md">
            <div
              className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg`}
            >
              <Dumbbell className={`w-8 h-8 ${colors.emptyStateIcon}`} />
            </div>
            <h3 className={`text-lg font-bold ${colors.textPrimary} mb-2`}>
              {t("exercisesPage.selectOrg.title")}
            </h3>
            <p className={colors.textTertiary}>
              {t("exercisesPage.selectOrg.message")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col lg:flex-row h-auto min-h-full lg:h-full ${colors.bgPrimary} p-3 sm:p-6 lg:space-x-6 overflow-y-auto lg:overflow-hidden custom-scrollbar`}
    >
      {/* Filters Sidebar */}
      <FilterSidebar
        isOpen={isFiltersOpen}
        onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
        filters={filterOptions}
      />

      {/* Main Content */}
      <div
        className={`flex-1 ${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg overflow-hidden flex flex-col min-h-[500px] lg:h-full`}
      >
        {/* Header */}
        <div
          className={`p-4 md:p-6 border-b ${colors.tableBorder} ${colors.tableHeader} flex-shrink-0`}
        >
          <PageHeader
            title={t("exercisesPage.title")}
            count={filteredCount}
            onRefresh={fetchExercises}
            onAdd={openCreateModal}
            addButtonText={t("exercisesPage.addExercise")}
            addButtonTextMobile={t("exercisesPage.addExerciseMobile")}
            loading={loading}
            actionLoading={actionLoading}
          />
        </div>

        {/* Data Table */}
        <DataTable
          data={paginatedExercises}
          columns={columns}
          loading={loading}
          actionLoading={actionLoading}
          error={error}
          successMessage={successMessage}
          onClearError={() => setError(null)}
          onClearSuccess={() => setSuccessMessage(null)}
          renderMobileCard={renderMobileCard}
          emptyState={{
            icon: Dumbbell,
            title: t("exercisesPage.emptyState.title"),
            message: searchTerm
              ? t("exercisesPage.emptyState.messageSearch")
              : t("exercisesPage.emptyState.messageDefault"),
            action: !searchTerm
              ? {
                  label: t("exercisesPage.addExercise"),
                  onClick: openCreateModal,
                  icon: Plus,
                }
              : null,
          }}
          pagination={{
            currentPage,
            totalPages,
            rowsPerPage,
            totalItems: filteredCount,
            startIndex,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
          }}
        />
      </div>

      {/* Exercise Modal */}
      <ExerciseModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingExercise(null);
        }}
        onSubmit={editingExercise ? handleUpdate : handleCreate}
        exercise={editingExercise}
        title={
          editingExercise
            ? t("modals.editExerciseTitle")
            : t("modals.addExerciseTitle")
        }
        loading={actionLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, exercise: null })}
        onConfirm={handleDelete}
        title={t("modals.deleteExerciseTitle")}
        message={t("modals.deleteExerciseMessage", {
          name: deleteConfirm.exercise?.name,
        })}
        confirmText={t("modals.confirmDelete")}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />

      {/* Review Status Confirmation Dialog */}
      <ConfirmDialog
        isOpen={reviewStatusConfirm.isOpen}
        onClose={() =>
          setReviewStatusConfirm({
            isOpen: false,
            exercise: null,
            newStatus: null,
          })
        }
        onConfirm={handleUpdateReviewStatus}
        title={
          reviewStatusConfirm.newStatus === "approved"
            ? t("modals.approveExerciseTitle")
            : t("modals.rejectExerciseTitle")
        }
        message={t(
          reviewStatusConfirm.newStatus === "approved"
            ? "modals.approveExerciseMessage"
            : "modals.rejectExerciseMessage",
          { name: reviewStatusConfirm.exercise?.name },
        )}
        confirmText={
          reviewStatusConfirm.newStatus === "approved"
            ? t("modals.confirmApprove")
            : t("modals.confirmReject")
        }
        confirmButtonClass={
          reviewStatusConfirm.newStatus === "approved"
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "bg-red-600 hover:bg-red-700 text-white"
        }
      />
    </div>
  );
};

export default ExercisesPage;
